// TauriApp.js - Desktop app for Tauri
// Modify this file and components in components/desktop/ for desktop app changes.
// DO NOT modify WebApp.js - that's the web application.

import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import FileBrowser from './components/desktop/FileBrowser';

const queryClient = new QueryClient();

const colors = {
  navy900: "#030711",
  navy800: "#060e1a",
  navy700: "#0a1628",
  navy600: "#0f1a2e",
  teal500: "#14b8a6",
  teal400: "#2dd4bf",
  teal300: "#5eead4",
  teal700: "#134e4a",
  gold400: "#fbbf24",
  white: "#ffffff",
  gray400: "#9ca3af",
};

function DesktopApp() {
  const { user, loading, isAuthenticated, login, logout } = useAuth();
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError("");
    try {
      await login(loginForm.username, loginForm.password);
    } catch (err) {
      setLoginError(err.response?.data?.detail || err.message || "Login failed");
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${colors.navy900} 0%, ${colors.navy800} 50%, ${colors.navy900} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: colors.white,
      }}>
        <div style={{textAlign: "center"}}>
          <div style={{
            width: "40px",
            height: "40px",
            border: `3px solid ${colors.teal500}`,
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 15px",
          }} />
          <div style={{color: colors.gray400}}>Loading...</div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${colors.navy900} 0%, ${colors.navy800} 50%, ${colors.navy900} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}>
        <div style={{
          width: "100%",
          maxWidth: "400px",
          backgroundColor: colors.navy700,
          borderRadius: "16px",
          padding: "40px",
          border: `1px solid ${colors.teal700}`,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
        }}>
          <div style={{textAlign: "center", marginBottom: "30px"}}>
            <h1 style={{
              fontSize: "28px",
              fontWeight: "bold",
              color: colors.white,
              marginBottom: "8px",
            }}>Virgo</h1>
            <p style={{color: colors.gray400, fontSize: "14px"}}>
              Sign in to access your cases
            </p>
          </div>

          {loginError && (
            <div style={{
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              color: "#fca5a5",
              padding: "12px",
              borderRadius: "8px",
              marginBottom: "20px",
              fontSize: "14px",
            }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{marginBottom: "16px"}}>
              <label style={{
                display: "block",
                color: colors.teal300,
                fontSize: "14px",
                marginBottom: "6px",
                fontWeight: "500",
              }}>
                Username
              </label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm(p => ({...p, username: e.target.value}))}
                placeholder="Enter username"
                required
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  backgroundColor: colors.navy800,
                  border: `1px solid ${colors.teal700}`,
                  borderRadius: "8px",
                  color: colors.white,
                  fontSize: "15px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{marginBottom: "24px"}}>
              <label style={{
                display: "block",
                color: colors.teal300,
                fontSize: "14px",
                marginBottom: "6px",
                fontWeight: "500",
              }}>
                Password
              </label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm(p => ({...p, password: e.target.value}))}
                placeholder="Enter password"
                required
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  backgroundColor: colors.navy800,
                  border: `1px solid ${colors.teal700}`,
                  borderRadius: "8px",
                  color: colors.white,
                  fontSize: "15px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              style={{
                width: "100%",
                padding: "14px",
                background: `linear-gradient(135deg, ${colors.teal500} 0%, ${colors.teal400} 100%)`,
                color: colors.navy900,
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: isLoggingIn ? "not-allowed" : "pointer",
                opacity: isLoggingIn ? 0.7 : 1,
                transition: "all 0.2s",
              }}
            >
              {isLoggingIn ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      background: `linear-gradient(135deg, ${colors.navy900} 0%, ${colors.navy800} 50%, ${colors.navy900} 100%)`,
    }}>
      {/* Header */}
      <div style={{
        padding: "12px 24px",
        backgroundColor: colors.navy800,
        borderBottom: `1px solid ${colors.teal700}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}>
        <div style={{display: "flex", alignItems: "center", gap: "12px"}}>
          <span style={{
            fontSize: "20px",
            fontWeight: "bold",
            color: colors.teal300,
          }}>Virgo</span>
          <span style={{color: colors.gray400, fontSize: "14px"}}>
            {user?.display_name || user?.username}
          </span>
        </div>
        <button
          onClick={logout}
          style={{
            padding: "8px 16px",
            backgroundColor: "transparent",
            border: `1px solid ${colors.teal700}`,
            color: colors.teal300,
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
            transition: "all 0.2s",
          }}
        >
          Sign Out
        </button>
      </div>

      {/* Main content */}
      <div style={{flex: 1, overflow: "auto"}}>
        <FileBrowser />
      </div>
    </div>
  );
}

function TauriApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DesktopApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default TauriApp;
